import Backbone from 'backbone'
import _ from 'lodash'
import $ from 'jquery'
import i18n from 'scoreboard/components/i18n'
import metadata from 'scoreboard/components/metadata'
import noPermission from 'scoreboard/components/no-permission.hbs'
import EmptyState from 'scoreboard/components/empty-state'
import appDisplay from 'scoreboard/components/app-display'
import chartsHeader from './charts-header'
import DrilldownManager from './drilldown-manager'
import renderChartDesigner from './render-chart-designer'
import renderChartDataTable from './render-chart-data-table'
import renderChartColorLegend from './render-chart-color-legend'
import serieDrilldown from './serie-drilldown'
import loadingDots from 'scoreboard/components/loading-dots.hbs'
import WidgetEmptyState from 'scoreboard/dashboards/widgets/widget-empty-state'
import template from './charts-view.hbs'
import constants from 'scoreboard/components/constants'
import guard from 'scoreboard/components/unsaved/guard'
import { DESIGNER_ARRAY_SETTINGS_ATTRIBUTES } from './edit/chart-designer-editor'

import getZoomToIds from './get-zoom-to-ids'

function incompleteReplicationContent() {
	let body = noPermission({message: i18n.get('common.replication.incompleteChart'),
		hideContactAdminMessage: true})
	return new EmptyState({ body })
}

const ChartsView = Backbone.View.extend({
	/**
	 * @type Chart
	 */
	model: null,

	initialize: function (options = {}) {
		this.options = options

		this.data = this.model.data

		this.header = chartsHeader.generate(this.subsection, this.model, this)
		this.firstRender = true
	},

	render: function () {
		if (this.model.chartDesigner.get('incompleteReplication')) {
			//#40975 special case for re-replication
			_.invoke(appDisplay.navView(), 'toggleEditViewState' ,false)
			let emptyState = incompleteReplicationContent().render()
			this.$el.append(emptyState.el)
			return emptyState
		}

		this.renderHeader()
		this.$el.append(template())

		this.renderSubsection(this.$('.scroll-container'))
		this.renderChartAndAccessories(true)

		// Charts can have custom aspect-ratios that prevent them from resizing
		// further if their aspect ratio doesn't allow it. If that's the case,
		// don't rerender the chart when resizing
		this.on('resize', () => {
			// If there's no chart yet, there's no need to set dimensions
			if (_.isNil(this.chart)) return

			this.setChartMaxHeight()
			this.setChartMinHeight()
			let container = this.$('.chart-container')[0]
			let lastData = this.chart.lastData
			if (lastData?.width !== container.offsetWidth ||
				lastData?.height !== container.offsetHeight) {
				let dimensions= {
					width: container.offsetWidth,
					height: container.offsetHeight
				}
				this.renderChart(dimensions)
			}
		})

		this.secure()
	},

	renderHeader: function () {
		this.listenTo(this.header, 'change:period', () => {
			this.fetchAndRender(this.fetchOptions(), () => this.renderChart())
		})

		this.$el.prepend(this.header.el)
		this.header.render()
	},

	setChartMaxHeight: function() {
		let legendHeight = this.$('.legend-aligned-vertical').length ?
			0 :
			this.$('.chart-legend-container').outerHeight() + constants.STANDARD_MARGIN

		let isSinglePeriodChart = _.includes(['pie','donut'],
			this.model.get('chartDesigner').plotType)
		if (isSinglePeriodChart) {
			this.$('.chart-and-legend').css('padding-bottom', legendHeight)
		} else {
			let maxHeight = this.$('.chart-ratio-handler').height()
			this.$('.chart-container').css('max-height', maxHeight - legendHeight)
		}
	},

	setChartMinHeight: function() {
		if (this.model.get('chartDesigner').plotType === 'timeline') {
			const rangeCount = this.data.plots()[0].breakdown.range.length
			if (rangeCount > 1) {
				let minHeight = rangeCount * 150 // min height of 150 per range
				this.$('.chart-container')
					.css('min-height', `${minHeight}px`)
					.parent()
					.css('overflow', 'visible')
				this.$('.scroll-container').css('overflow', 'auto')
				// Add 20 to keep gap between table and chart
				this.$('.chart-row').css('min-height', minHeight + 20 + 'px')
			}
		}
	},

	secure: function() {
		this.header.conditionallyHideEditTab(this.model.isEditable())
	},

	/**
	 * Fetch data and render the chart after fetch is done.
	 * @param {Object} options Fetch options to be passed in
	 * @param {*} render The render function to call when the fetch is done
	 */
	fetchAndRender(options, render) {
		this.toggleLoading(true)
		return this.data.fetch(options).then(response => {
			this.toggleLoading(false)

			updateDesignerFromFetch({
				oldDesigner: this.model.chartDesigner,
				updatedDesigner: response.updatedChartDesigner
			})

			render(response)
			this.renderChartAndAccessories()
		})
	},

	toggleLoading: function (loading) {
		let containers = this.$('.chart-container, .chart-table-container')

		// If changes to the chart were done rapidly (i.e. adding items, changing
		// the period), make sure the previous timeout is cleared so that they
		// don't  stack up.
		if (this.loadingTimeout) {
			clearTimeout(this.loadingTimeout)
		}

		if (loading) {
			// Prevent a flicker on the chart caused by the loading dots when a fast
			// connection almost instantly gets the data.
			this.loadingTimeout = setTimeout(() => {
				containers.addClass('is-loading')
				containers.append(loadingDots())
			}, constants.LOADING_TIME)
		} else {
			clearTimeout(this.loadingTimeout)
			this.$('.loading-dots').remove()
			containers.removeClass('is-loading')
		}
	},

	renderChart: function (dimensions, emphasize = null) {
		if (this.model.chartDesigner.hasError()) {
			this.renderError()
			return
		} else {
			this.removeEmptyState()
		}
		if (this.chart) {
			this.chart.destroyTooltip()
		}

		let chartOptions = {
			chartDesigner: this.model.chartDesigner,
			chartData: this.model.chartDesigner.data,
			container: this.$('.chart-container'),
			dimensions,
			emphasize,
			zoomToIds: getZoomToIds({
				firstRender: this.firstRender,
				state: this.options.exportState,
				chartData: this.model.chartDesigner.data
			})
		}

		if (this.chart) {
			chartOptions.chartViewObject = this.chart
			renderChartDesigner(chartOptions)
			this.handleSecondRender()
		} else {
			this.setChartMaxHeight()
			this.setChartMinHeight()

			this.chart = renderChartDesigner(chartOptions)

			if (this.chart) {
				this.drilldownManager = new DrilldownManager({
					chartDesigner: this.model.chartDesigner,
					chartViewObject: this.chart
				})

				this.listenTo(this.drilldownManager, 'drilldown',
					drilldown => this.onDrilldown(drilldown))

				this.listenTo(this.drilldownManager, 'back',
					drilldown => this.onBack(drilldown))

				this.listenTo(this.drilldownManager, 'goto',
					({ serie }) => this.onNav({ serie }))

				this.listenTo(this.drilldownManager, 'rerender',
					() => this.renderChart())

				this.handleSecondRender()
			}
		}

		this.firstRender = false
	},

	/**
	 * #41664 when the chart is rendered, this will check if there is data for
	 * a second render. If so, it will setup an event listener to wait for the
	 * first render to finish its animation, then render the second animation
	 */
	handleSecondRender: function() {
		if (this.chart.secondRenderData) {
			this.chart.once('transition-end', () => {
				console.log('Rendering second change')
				renderChartDesigner(_.extend(
					this.chart.secondRenderData, { isSecondRender: true }))
				delete this.chart.secondRenderData
			})
		}
	},

	/**
	 * Renders the chart and its accessories (data table and legends)
	 *
	 * @param {Boolean} renderChart indicates whether the chart itself should be
	 * rendered
	 */
	renderChartAndAccessories: function (renderChart) {
		this.renderDataTable()
		this.renderColorLegend()
		// Only render the chart when rendering the view or if the table was toggled
		// on/off to redraw the chart to the correct height,
		// Make sure the chart is only re-rendered when the table is toggled to
		// prevent breaking transitions.
		if (renderChart) {
			this.renderChart()
		}
	},

	renderDataTable: function() {
		this.removeDataTable()
		const container = this.$('.chart-table-container .panel-content')
		this.dataTable = renderChartDataTable(container, this.model.chartDesigner)
		this.$el.toggleClass('is-showing-table', !!this.dataTable)
	},

	renderColorLegend: function() {
		this.removeColorLegend()
		const container = this.$('.chart-legend-container')
		this.colorLegend = renderChartColorLegend(container, this.model.chartDesigner)
		this.colorLegend && this.colorLegend.on('emphasize deemphasize',
			this.emphasize.bind(this))
	},

	emphasize: function(seriesId) {
		let chartContainer = this.$('.chart-container')[0]
		let dimensions= {
			width: chartContainer.offsetWidth,
			height: chartContainer.offsetHeight
		}
		this.renderChart(dimensions, seriesId)
	},

	/**
	 * #38916 if an error is found in the chartDesigner, render an error icon in
	 * place of the chart
	 */
	renderError: function() {
		this.removeEmptyState()
		this.$('.chart-container').empty()

		this.emptyState = new WidgetEmptyState({
			errorResponse: this.errorToDisplay(),
			container: $(this.el),
			widgetType: 'chart',
			showErrorTooltip: false,
			hasStandardIconSize: true
		})
		this.$('.chart-container').append(this.emptyState.el)
		this.emptyState.render()
	},

	errorToDisplay () {
		return _.first(this.model.chartDesigner.errors())
	},

	getHeaderText: function () {
		return this.model.isNew() ?
			i18n.get('common.create2', metadata[this.model.section].createTypeCode) :
			this.model.get('label')
	},

	/**
	 * @abstract
	 */
	fetchOptions: function () {},

	/**
	 * Implemented by subclass to handle specific rendering for that section
	 * (ie the View or Edit tabs)
	 *
	 * @abstract
	 *
	 * @param {jQuery} container - a jQuery selection containing the DOM node
	 * that subsection-specific items should be appended to
	 */
	renderSubsection: function (container) {},

	getState: function (options = {}) {
		return _.extend({
			zoomToIds: this.model.chartDesigner.data.currentDrilldown()?.zoomToIds()
		}, this.uiState)
	},

	onDrilldown: function (drilldown) {
		// if this is a leaf node, we will just rerender the chart with
		// different topojson. We don't want to change the drilldown or fetch
		// new data
		//
		// i.e. if we are viewing a Texas leaf node, we want the current
		// drilldown to stay as the United States so the legend can maintain
		// values from the surrounding states
		this.model.chartDesigner.data.currentDrilldown(drilldown)
		if (_.isNil(drilldown) || drilldown.isZoomed()) {
			this.renderChart()
			return
		}

		let dataset = this.model.chartDesigner.startingDataset()
		let liveFilters = {}
		if (!!dataset) liveFilters[dataset.id] = drilldown.drilldownFilters

		this.uiState = {
			liveFilters,
			drilldownId: drilldown.drilldownId
		}

		return this.fetchAndRender({
			chart: this.model.chartDesigner,
			liveFilters,
			drilldownId: drilldown.drilldownId,
			drilldown
		}, () => this.renderChart())
	},

	onBack: function (drilldown) {
		// TODO I think "back" will need some special handling, but if I don't
		// need it, just combine onBack and onDrilldown
		this.onDrilldown(drilldown)
	},

	onNav: function({ serie }) {
		guard.conditionallyRun(() => {
			serieDrilldown({
				chartDesigner: this.model,
				serie
			})
		})
	},

	removeDataTable: function () {
		_.invoke(this.dataTable, 'remove')
		delete this.dataTable
	},

	removeColorLegend: function() {
		_.invoke(this.colorLegend, 'remove')
		delete this.colorLegend
	},

	destroyChart: function() {
		this.chart && this.chart.destroy()
		delete this.chart
	},

	removeEmptyState() {
		this.emptyState && this.emptyState.remove()
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([ this.header, this.drilldownManager ], 'remove')
		this.destroyChart()
		this.removeEmptyState()
		this.removeDataTable()
		this.removeColorLegend()
	}
})

function updateDesignerFromFetch ({ oldDesigner, updatedDesigner }) {
	if (!oldDesigner) {
		return
	}

	oldDesigner.breakdowns.forEach(oldBreakdown => {
		// For each breakdown model, update the 'error' and 'valueSuggestions'
		// with the response from the server

		let updatedBreakdown = _.find(updatedDesigner.breakdowns, b => {
			let idField = _.isNil(oldBreakdown.id) ? 'tempId' : 'id'
			return oldBreakdown.get(idField) == b[idField]
		})

		if (updatedBreakdown) {
			oldBreakdown.set({
				error: updatedBreakdown.error,
				valueSuggestions: updatedBreakdown.valueSuggestions
			})

			oldBreakdown.sortSettings.set(updatedBreakdown.sortSettings)
		}
	})

	// If a new plotType was used, the server may be passing back new settings
	// for colors, labels, sorting, etc
	DESIGNER_ARRAY_SETTINGS_ATTRIBUTES.forEach(setting => {
		oldDesigner.set(setting, updatedDesigner[setting])
	})
	oldDesigner.leftAxisBarSortSettings.set(updatedDesigner.leftAxisBarSortSettings)
	oldDesigner.rightAxisBarSortSettings.set(updatedDesigner.rightAxisBarSortSettings)

	// Make sure any new errors are accounted for
	oldDesigner.applyErrors(updatedDesigner)
}

export default ChartsView
export { updateDesignerFromFetch }
export { incompleteReplicationContent }

ChartsView.__filename__ = 'charts/charts-view.js'
